
import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Users } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

type Service = {
  id: string;
  title: string;
};

interface PollUsersDialogProps {
  service: Service;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPollCreated: () => void;
}

export const PollUsersDialog: React.FC<PollUsersDialogProps> = ({
  service,
  open,
  onOpenChange,
  onPollCreated,
}) => {
  const { user } = useAuth();
  const [creating, setCreating] = useState(false);
  const [reason, setReason] = useState("");

  const handleCreatePoll = async () => {
    if (!user || !reason.trim()) return;

    try {
      setCreating(true);
      
      const { error } = await supabase
        .from("service_deletion_polls")
        .insert({
          service_id: service.id,
          initiated_by: user.id,
        });

      if (error) throw error;

      toast.success("Community poll created successfully");
      onPollCreated();
      onOpenChange(false);
      setReason("");
    } catch (error) {
      console.error("Error creating poll:", error);
      toast.error("Failed to create community poll");
    } finally {
      setCreating(false);
    }
  };

  const handleCancel = () => {
    setReason("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-500" />
            Create Community Poll
          </DialogTitle>
          <DialogDescription>
            Start a community vote to decide whether "{service.title}" should be removed from the platform.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <Label htmlFor="reason">Reason for poll (optional)</Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain why this service should be reviewed by the community..."
              rows={3}
            />
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              <strong>Poll Details:</strong>
              <br />
              • Duration: 7 days
              <br />
              • All registered users can vote
              <br />
              • Service remains active during voting
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={creating}>
            Cancel
          </Button>
          <Button onClick={handleCreatePoll} disabled={creating}>
            {creating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating Poll...
              </>
            ) : (
              "Create Poll"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
